import React, { useState } from 'react';
import { Modal, Form, Select, Button } from 'antd';
import { HubType } from '../graphql/queries/getHubList';

export interface AddRouteFormData {
    originHub: string;
    destinationHub: string;
}

interface AddRouteModalProps {
    visible: boolean;
    onCancel: () => void;
    onOk: (data: AddRouteFormData) => Promise<void>;
    loading?: boolean;
    hubs: HubType[];
}

const AddRouteModal: React.FC<AddRouteModalProps> = ({
    visible,
    onCancel,
    onOk,
    loading = false,
    hubs
}) => {
    const [form] = Form.useForm();
    const [selectedOriginHub, setSelectedOriginHub] = useState<string>('');
    const [selectedDestinationHub, setSelectedDestinationHub] = useState<string>('');

    // HUB选项 - 从warehouse表读取site_type=1的数据
    const hubOptions = hubs.map(hub => ({
        label: hub.sortingCode!!,
        value: hub.id
    }));

    // 目的地选项 - 排除已选择的始发地
    const destinationOptions = hubOptions.map(option => ({
        ...option,
        disabled: option.value === selectedOriginHub
    }));

    // 始发地选项 - 排除已选择的目的地
    const originOptions = hubOptions.map(option => ({
        ...option,
        disabled: option.value === selectedDestinationHub
    }));

    const handleOriginHubChange = (value: string) => {
        setSelectedOriginHub(value);
        // 如果目的地是当前选择的始发地，清空目的地
        if (selectedDestinationHub === value) {
            setSelectedDestinationHub('');
            form.setFieldsValue({ destinationHub: undefined });
        }
    };

    const handleDestinationHubChange = (value: string) => {
        setSelectedDestinationHub(value);
        // 如果始发地是当前选择的目的地，清空始发地
        if (selectedOriginHub === value) {
            setSelectedOriginHub('');
            form.setFieldsValue({ originHub: undefined });
        }
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            await onOk(values);
            // 只有成功提交后才清空表单
            form.resetFields();
            setSelectedOriginHub('');
            setSelectedDestinationHub('');
        } catch (error) {
            console.error('Add route failed:', error);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setSelectedOriginHub('');
        setSelectedDestinationHub('');
        onCancel();
    };

    return (
        <Modal
            title="Add Transit Route"
            open={visible}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Cancel
                </Button>,
                <Button
                    key="confirm"
                    type="primary"
                    loading={loading}
                    onClick={handleOk}
                >
                    Confirm
                </Button>
            ]}
            width={500}
        >
            <Form
                form={form}
                layout="vertical"
                requiredMark={true}
            >
                <Form.Item
                    label="Origin Hub"
                    name="originHub"
                    rules={[
                        { required: true, message: 'Please select origin hub' }
                    ]}
                >
                    <Select
                        placeholder="Select origin hub"
                        options={originOptions}
                        onChange={handleOriginHubChange}
                        showSearch
                        allowClear
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                    />
                </Form.Item>

                <Form.Item
                    label="Destination Hub"
                    name="destinationHub"
                    rules={[
                        { required: true, message: 'Please select destination hub' }
                    ]}
                >
                    <Select
                        placeholder="Select destination hub"
                        options={destinationOptions}
                        onChange={handleDestinationHubChange}
                        showSearch
                        allowClear
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default AddRouteModal;
